import { useState } from 'react';
import { Pagination, Popconfirm } from 'antd';

import { useApiMutation } from '@/api/mutations';
import { purchaseOrdersKey } from '@/api/purchaseOrders';
import {
  acceptBillDifferences,
  cancelPurchaseBill,
  createPurchaseBill,
  purchaseBillsKey,
  useBillable,
  usePurchaseBill,
  usePurchaseBills,
  type PurchaseBill,
  type PurchaseBillLine,
  type PurchaseBillSummary,
} from '@/api/purchaseBills';
import { itemUnitsKey, useVendors } from '@/api/masters';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { DatePicker } from '@/components/DatePicker';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import {
  DocumentForm,
  DocumentFormActions,
  DocumentFormFoot,
  DocumentFormHead,
  DocumentHeading,
  DocumentLines,
  DocumentPaper,
  DocumentParties,
  DocumentTop,
  DocumentTotals,
  FormSectionTitle,
  LineName,
  type DocumentColumn,
} from '@/components/DocumentSheet';
import { Field, controlClass, textAreaClass } from '@/components/Field';
import { IconAction } from '@/components/IconAction';
import { Button, Card, EmptyState, Notice, PageHeading } from '@/components/PageHeading';
import { SearchPicker, type PickerOption } from '@/components/SearchPicker';
import { StatusBadge } from '@/components/StatusBadge';
import { useDebounced } from '@/components/useDebounced';
import { formatDocumentDate, toDayjs, toIsoDate } from '@core/format/dates';
import { formatAmount } from '@core/format/money';
import {
  addCharge,
  addReceiptLine,
  billProblems,
  billStatusLabel,
  billStatusTone,
  billSubtotal,
  differenceText,
  emptyBill,
  maxBillQuantity,
  removeBillLine,
  toBillRequest,
  updateBillLine,
  type BillDraft,
} from '@core/purchasing/bill';
import { can, canAct } from '@core/session/capability';
import type { Session } from '@core/session/types';

const PAGE_SIZE = 25;

type Screen = { kind: 'list' } | { kind: 'new' } | { kind: 'view'; id: number };

/**
 * Commerce › Supplier bills (spec §16, step 3 of `docs/Purchase_Sale_Outstanding_Design.md`) — each
 * supplier's paper bill entered against what they delivered. The tax, the totals, the due date and
 * the differences are the server's: the bill page shows them, it never works them out.
 */
export function PurchaseBillsPage({
  session,
  openId,
  onExit,
}: {
  session: Session;
  /** Opens on this one — the purchase desk's history. */
  openId?: number | undefined;
  /** Where Back from it goes instead of the list — the desk. */
  onExit?: (() => void) | undefined;
}) {
  const [screen, setScreen] = useState<Screen>(() =>
    openId === undefined ? { kind: 'list' } : { kind: 'view', id: openId },
  );
  const toList = () => (onExit ? onExit() : setScreen({ kind: 'list' }));
  const [today] = useState(() => toIsoDate(new Date()) ?? '');
  const bill = usePurchaseBill(screen.kind === 'view' ? screen.id : null);

  const save = useApiMutation<PurchaseBill, BillDraft>({
    mutationFn: (draft) => createPurchaseBill(toBillRequest(draft)),
    success: (saved) => `${saved.documentNumber} entered.`,
    invalidates: [purchaseBillsKey, purchaseOrdersKey, itemUnitsKey],
    onSuccess: (saved) => setScreen({ kind: 'view', id: saved.id }),
  });

  if (screen.kind === 'new') {
    return (
      <BillEditor
        session={session}
        today={today}
        busy={save.isPending}
        onClose={() => toList()}
        onSave={(draft) => void save.mutateAsync(draft).catch(() => undefined)}
      />
    );
  }

  if (screen.kind === 'view') {
    return bill.data ? (
      <BillView session={session} bill={bill.data} onBack={() => toList()} />
    ) : null;
  }

  return (
    <BillList
      session={session}
      onNew={() => setScreen({ kind: 'new' })}
      onOpen={(id) => setScreen({ kind: 'view', id })}
    />
  );
}

/* ------------------------------------------------------------------------------------------------ */

function BillList({
  session,
  onNew,
  onOpen,
}: {
  session: Session;
  onNew: () => void;
  onOpen: (id: number) => void;
}) {
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const debounced = useDebounced(search);
  const bills = usePurchaseBills(session.branch?.id, {
    includeCancelled,
    search: debounced || undefined,
    page,
    pageSize: PAGE_SIZE,
  });
  const total = bills.data?.total ?? 0;

  const columns: DataViewColumn<PurchaseBillSummary>[] = [
    {
      title: 'Bill',
      dataIndex: 'supplierBillNumber',
      primary: true,
      render: (_, row) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{row.supplierBillNumber}</div>
          <div className="text-default font-mono-nums text-[12px]">
            {row.documentNumber} · {formatDocumentDate(row.billDate)}
          </div>
        </div>
      ),
    },
    {
      title: 'Supplier',
      dataIndex: 'supplierName',
      render: (_, row) => <span className="text-dark text-[13px]">{row.supplierName}</span>,
    },
    {
      title: 'Due',
      dataIndex: 'dueDate',
      render: (_, row) => (
        <span className="text-dark text-[13px]">{formatDocumentDate(row.dueDate)}</span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (_, row) => (
        <div className="flex flex-wrap gap-1">
          <StatusBadge tone={billStatusTone[row.status]}>{billStatusLabel[row.status]}</StatusBadge>
          {row.hasDifferences && <StatusBadge tone="warning">Differences</StatusBadge>}
        </div>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'billTotal',
      render: (_, row) => (
        <span className="font-mono-nums text-dark text-[13px]">{formatAmount(row.billTotal)}</span>
      ),
    },
    {
      title: '',
      dataIndex: 'id',
      trailing: true,
      render: (_, row) => (
        <IconAction
          icon="icon icon-chevron-right"
          label={`Open ${row.documentNumber}`}
          onClick={() => onOpen(row.id)}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeading
        title="Supplier bills"
        subtitle="Each supplier's bill entered off their paper, against what they delivered. Differences from the order or the delivery are flagged, not refused."
        actions={
          canAct(session, 'PurchaseBill:Create') ? (
            <Button variant="primary" icon="icon icon-plus" shortcut="create" onClick={onNew}>
              Enter a bill
            </Button>
          ) : undefined
        }
      />

      <Card>
        <div className="border-border-color flex flex-wrap items-center gap-3 border-b pb-3">
          <label className="text-default inline-flex cursor-pointer items-center gap-2 text-[12px]">
            <input
              type="checkbox"
              checked={includeCancelled}
              onChange={(event) => {
                setIncludeCancelled(event.target.checked);
                setPage(1);
              }}
              className="accent-primary size-3.5 cursor-pointer"
            />
            Show cancelled
          </label>
          <input
            className="border-border-color min-w-56 flex-1 rounded-md border bg-white px-3 py-1.5 text-[13px]"
            placeholder="Their bill number, or ours"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
        </div>

        <DataView<PurchaseBillSummary>
          columns={columns}
          dataSource={bills.data?.items ?? []}
          loading={bills.isLoading}
          empty={
            <EmptyState
              icon="icon icon-file-text"
              title={debounced ? 'Nothing matches that' : 'No supplier bill entered yet'}
              hint={
                debounced
                  ? 'Clear the search.'
                  : 'Enter a bill when the supplier sends it, against the deliveries it covers.'
              }
            />
          }
        />

        {total > PAGE_SIZE && (
          <div className="border-border-color flex justify-end border-t py-3">
            <Pagination
              current={page}
              total={total}
              pageSize={PAGE_SIZE}
              onChange={setPage}
              showSizeChanger={false}
              showTotal={(count) => `${count} bills`}
            />
          </div>
        )}
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------------------------------------ */

/**
 * Entering a bill — a full page. The supplier first, because what can be billed is what they
 * delivered; then each delivery line picked onto the bill, a charge such as freight if there is one,
 * and the numbers off their paper.
 */
function BillEditor({
  session,
  today,
  busy,
  onClose,
  onSave,
}: {
  session: Session;
  today: string;
  busy: boolean;
  onClose: () => void;
  onSave: (draft: BillDraft) => void;
}) {
  const [draft, setDraft] = useState<BillDraft>(() => emptyBill(today));
  const [supplier, setSupplier] = useState<PickerOption | null>(null);
  const [supplierSearch, setSupplierSearch] = useState('');
  const suppliers = useVendors(session.branch?.id, {
    capability: 'Supplier',
    search: supplierSearch,
    pageSize: 10,
  });
  const billable = useBillable(draft.supplierVendorId);
  const problems = billProblems(draft);
  const fields = useAutoFocusFirstField<HTMLDivElement>();
  const onBill = new Set(draft.lines.map((line) => line.goodsReceiptLineId));

  const number = (value: string) => (value === '' ? null : Number(value));

  const lineNumber = (line: BillDraft['lines'][number]) => draft.lines.indexOf(line) + 1;

  const columns: DocumentColumn<BillDraft['lines'][number]>[] = [
    {
      key: 'description',
      title: 'Description',
      render: (line) => (
        <div className="flex min-w-48 flex-col gap-1">
          <input
            aria-label={`Line ${lineNumber(line)} description`}
            value={line.description}
            disabled={busy || line.goodsReceiptLineId !== null}
            onChange={(event) =>
              setDraft((was) => updateBillLine(was, line.key, { description: event.target.value }))
            }
            className={controlClass}
          />
          {line.detail && (
            <span className="text-default text-[length:var(--scale-list-meta)]">{line.detail}</span>
          )}
        </div>
      ),
    },
    {
      key: 'hsn',
      title: 'HSN',
      render: (line) => (
        <input
          aria-label={`Line ${lineNumber(line)} HSN`}
          value={line.hsnCode}
          disabled={busy}
          onChange={(event) =>
            setDraft((was) => updateBillLine(was, line.key, { hsnCode: event.target.value }))
          }
          className={`${controlClass} font-mono-nums max-w-28`}
        />
      ),
    },
    {
      key: 'quantity',
      title: 'Qty',
      numeric: true,
      render: (line) => (
        <input
          type="number"
          aria-label={`Line ${lineNumber(line)} quantity`}
          onWheel={(event) => event.currentTarget.blur()}
          min={1}
          max={maxBillQuantity}
          value={line.quantity}
          disabled={busy}
          onChange={(event) =>
            setDraft((was) =>
              updateBillLine(was, line.key, { quantity: Number(event.target.value) }),
            )
          }
          className={`${controlClass} max-w-20 text-right`}
        />
      ),
    },
    {
      key: 'rate',
      title: 'Rate (₹)',
      numeric: true,
      render: (line) => (
        <input
          type="number"
          aria-label={`Line ${lineNumber(line)} rate`}
          onWheel={(event) => event.currentTarget.blur()}
          min={0}
          step="0.01"
          value={line.rate ?? ''}
          disabled={busy}
          onChange={(event) =>
            setDraft((was) => updateBillLine(was, line.key, { rate: number(event.target.value) }))
          }
          className={`${controlClass} max-w-32 text-right`}
        />
      ),
    },
    {
      key: 'gst',
      title: 'GST %',
      numeric: true,
      render: (line) => (
        <input
          type="number"
          aria-label={`Line ${lineNumber(line)} GST rate`}
          onWheel={(event) => event.currentTarget.blur()}
          min={0}
          max={100}
          step="0.01"
          value={line.taxRatePct ?? ''}
          disabled={busy}
          onChange={(event) =>
            setDraft((was) =>
              updateBillLine(was, line.key, { taxRatePct: number(event.target.value) }),
            )
          }
          className={`${controlClass} max-w-20 text-right`}
        />
      ),
    },
    {
      key: 'remove',
      title: '',
      render: (line) => (
        <button
          type="button"
          disabled={busy}
          onClick={() => setDraft((was) => removeBillLine(was, line.key))}
          aria-label={`Remove line ${lineNumber(line)}`}
          className="bg-danger-transparent text-danger hover:bg-danger inline-flex size-7 cursor-pointer items-center justify-center rounded-md transition-colors hover:text-white disabled:cursor-not-allowed"
        >
          <i className="icon icon-trash-2" />
        </button>
      ),
    },
  ];

  return (
    <>
      <DocumentHeading
        trail="Supplier bills"
        current="New"
        title="Enter a supplier's bill"
        onBack={onClose}
      />

      <div ref={fields} {...enterKeyScope}>
        <DocumentForm>
          <DocumentFormHead
            left={
              <>
                <FormSectionTitle>Supplier</FormSectionTitle>
                <Field label="Billed by" hint="Only vendors marked as a supplier are listed.">
                  <SearchPicker
                    value={supplier}
                    options={(suppliers.data?.items ?? []).map((vendor) => ({
                      id: String(vendor.id),
                      primary: vendor.name,
                      secondary: vendor.mobile,
                    }))}
                    total={suppliers.data?.total ?? 0}
                    loading={suppliers.isLoading}
                    disabled={busy}
                    placeholder="Search suppliers"
                    onSearch={setSupplierSearch}
                    onSelect={(option) => {
                      setSupplier(option);
                      // Another supplier's deliveries cannot be on this bill, so the lines go too.
                      setDraft((was) => ({
                        ...was,
                        supplierVendorId: option === null ? null : Number(option.id),
                        lines: [],
                      }));
                    }}
                  />
                </Field>
              </>
            }
            right={
              <>
                <FormSectionTitle>Their bill</FormSectionTitle>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Their bill number">
                    <input
                      value={draft.supplierBillNumber}
                      disabled={busy}
                      onChange={(event) =>
                        setDraft((was) => ({ ...was, supplierBillNumber: event.target.value }))
                      }
                      className={controlClass}
                    />
                  </Field>
                  <Field label="Bill date">
                    <DatePicker
                      className="w-full"
                      value={toDayjs(draft.billDate) ?? null}
                      disabled={busy}
                      onChange={(value) =>
                        setDraft((was) => ({ ...was, billDate: toIsoDate(value) ?? was.billDate }))
                      }
                    />
                  </Field>
                  <Field
                    label="Total on their paper (₹)"
                    hint="Compared with what the lines add up to."
                  >
                    <input
                      type="number"
                      onWheel={(event) => event.currentTarget.blur()}
                      min={0}
                      step="0.01"
                      value={draft.paperTotal ?? ''}
                      disabled={busy}
                      onChange={(event) =>
                        setDraft((was) => ({ ...was, paperTotal: number(event.target.value) }))
                      }
                      className={controlClass}
                    />
                  </Field>
                  <Field label="GST % for new lines" hint="Each line can still differ.">
                    <input
                      type="number"
                      onWheel={(event) => event.currentTarget.blur()}
                      min={0}
                      max={100}
                      step="0.01"
                      value={draft.defaultTaxRatePct ?? ''}
                      disabled={busy}
                      onChange={(event) =>
                        setDraft((was) => ({
                          ...was,
                          defaultTaxRatePct: number(event.target.value),
                        }))
                      }
                      className={controlClass}
                    />
                  </Field>
                </div>
              </>
            }
          />

          {draft.supplierVendorId !== null && (
            <div className="border-border-color mb-6 border-b pb-6">
              <FormSectionTitle>Delivered, not yet billed</FormSectionTitle>
              {(billable.data ?? []).length === 0 ? (
                <p className="text-default text-[length:var(--scale-list-meta)]">
                  {billable.isLoading
                    ? 'Loading…'
                    : 'Nothing from this supplier is waiting to be billed.'}
                </p>
              ) : (
                <ul className="mt-2 flex list-none flex-col gap-1.5 ps-0">
                  {(billable.data ?? []).map((line) => (
                    <li
                      key={line.goodsReceiptLineId}
                      className="border-border-color flex list-none flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="text-[length:var(--scale-list-name)] font-semibold text-gray-900">
                          {line.itemName} · {line.variantDisplayName}
                        </p>
                        <p className="text-default text-[length:var(--scale-list-meta)]">
                          {line.goodsReceiptNumber} · {formatDocumentDate(line.receivedOn)}
                          {line.supplierReference
                            ? ` · challan ${line.supplierReference}`
                            : ''} · {line.unbilled} of {line.received} to bill
                        </p>
                      </div>
                      <Button
                        disabled={busy || onBill.has(line.goodsReceiptLineId)}
                        onClick={() => setDraft((was) => addReceiptLine(was, line))}
                      >
                        {onBill.has(line.goodsReceiptLineId) ? 'On the bill' : 'Add'}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <FormSectionTitle
            aside={
              <Button
                icon="icon icon-plus"
                disabled={busy || draft.supplierVendorId === null}
                onClick={() => setDraft(addCharge)}
              >
                Add a charge
              </Button>
            }
          >
            Items
          </FormSectionTitle>

          <DocumentLines
            columns={columns}
            rows={draft.lines}
            rowKey={(line) => line.key}
            empty="Add the deliveries this bill is for, and any charge such as freight."
          />

          <DocumentFormFoot
            left={
              <Field label="Notes">
                <textarea
                  rows={4}
                  value={draft.notes}
                  disabled={busy}
                  onChange={(event) => setDraft((was) => ({ ...was, notes: event.target.value }))}
                  className={textAreaClass}
                />
              </Field>
            }
            right={
              <>
                <DocumentTotals
                  rows={[
                    {
                      label: 'Before tax',
                      value: `₹${formatAmount(billSubtotal(draft))}`,
                      grand: true,
                    },
                  ]}
                />
                <p className="text-default text-end text-[length:var(--scale-list-meta)]">
                  CGST and SGST, or IGST for a supplier in another state, are worked out on save
                  from each line&apos;s rate.
                </p>
                {problems.length > 0 && (
                  <Notice tone="warning" title="Not ready to save">
                    <ul className="mt-1 flex list-disc flex-col gap-0.5 ps-4">
                      {problems.slice(0, 6).map((problem) => (
                        <li key={problem}>{problem}</li>
                      ))}
                      {problems.length > 6 && <li>…and {problems.length - 6} more.</li>}
                    </ul>
                  </Notice>
                )}
              </>
            }
          />

          <DocumentFormActions>
            <span className="text-default me-auto text-[length:var(--scale-list-meta)]">
              Type it as it is on their paper. GST is worked out when it is saved.
            </span>
            <Button variant="secondary" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              icon="icon icon-check"
              data-enter-key-commit
              shortcut="save"
              loading={busy}
              disabled={busy || problems.length > 0}
              onClick={() => onSave(draft)}
            >
              {busy ? 'Saving…' : 'Save bill'}
            </Button>
          </DocumentFormActions>
        </DocumentForm>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------------------------------------ */

function BillView({
  session,
  bill,
  onBack,
}: {
  session: Session;
  bill: PurchaseBill;
  onBack: () => void;
}) {
  const act = useApiMutation<PurchaseBill, { how: 'cancel' | 'accept'; reason: string }>({
    mutationFn: ({ how, reason }) =>
      how === 'cancel'
        ? cancelPurchaseBill(bill.id, reason)
        : acceptBillDifferences(bill.id, reason),
    success: (saved, { how }) =>
      how === 'cancel' ? `${saved.documentNumber} cancelled.` : 'Differences accepted.',
    invalidates: [purchaseBillsKey, purchaseOrdersKey],
  });
  const cancelled = bill.status === 'Cancelled';

  const columns: DocumentColumn<PurchaseBillLine>[] = [
    {
      key: 'description',
      title: 'Description',
      render: (line) => (
        <LineName
          name={line.description}
          note={
            [
              line.goodsReceiptNumber,
              line.variantDisplayName,
              line.hsnCode && `HSN ${line.hsnCode}`,
            ]
              .filter(Boolean)
              .join(' · ') || 'Charge'
          }
        />
      ),
    },
    { key: 'qty', title: 'Qty', numeric: true, render: (line) => line.quantity },
    { key: 'rate', title: 'Rate', numeric: true, render: (line) => `₹${formatAmount(line.rate)}` },
    { key: 'gst', title: 'GST', numeric: true, render: (line) => `${line.taxRatePct}%` },
    {
      key: 'amount',
      title: 'Amount',
      numeric: true,
      render: (line) => <span className="font-semibold">₹{formatAmount(line.lineTotal)}</span>,
    },
  ];

  const money = (value: number) => `₹${formatAmount(value)}`;

  return (
    <>
      <DocumentHeading
        trail="Supplier bills"
        current={bill.documentNumber}
        title="Supplier bill"
        onBack={onBack}
        actions={
          !cancelled && canAct(session, 'PurchaseBill:Edit') ? (
            <ReasonAction
              label="Cancel bill"
              title="Cancel this bill?"
              hint="Its number stays in the sequence, and their bill number is freed to be entered again."
              busy={act.isPending}
              onConfirm={(reason) => act.mutate({ how: 'cancel', reason })}
            />
          ) : undefined
        }
      />

      {cancelled && (
        <div className="mb-3">
          <Notice
            tone="info"
            title={`Cancelled${bill.cancelledOn ? ` on ${formatDocumentDate(bill.cancelledOn)}` : ''}`}
          >
            {bill.cancelReason}
          </Notice>
        </div>
      )}

      {bill.differences.length > 0 && (
        <div className="mb-3">
          <Notice tone="warning" title="This bill does not match">
            <ul className="mt-1 flex list-disc flex-col gap-0.5 ps-4">
              {bill.differences.map((difference) => (
                <li key={`${difference.kind}-${difference.lineIndex ?? 'bill'}`}>
                  {differenceText(difference, bill)}
                </li>
              ))}
            </ul>
            {canAct(session, 'PurchaseBill:Approve') && (
              <div className="mt-2">
                <ReasonAction
                  label="Accept as it is"
                  title="Accept these differences?"
                  hint="The bill stands as entered; the reason is kept with it."
                  busy={act.isPending}
                  danger={false}
                  onConfirm={(reason) => act.mutate({ how: 'accept', reason })}
                />
              </div>
            )}
          </Notice>
        </div>
      )}

      {bill.differencesAcceptedOn && (
        <div className="mb-3">
          <Notice
            tone="info"
            title={`Differences accepted on ${formatDocumentDate(bill.differencesAcceptedOn)}`}
          >
            {bill.differencesAcceptReason}
          </Notice>
        </div>
      )}

      <DocumentPaper>
        <DocumentTop
          issuer={bill.supplierName}
          issuerLines={[bill.supplierIsMsme ? 'MSME supplier' : null]}
          title="Supplier bill"
          number={bill.supplierBillNumber}
          status={{ label: billStatusLabel[bill.status], tone: billStatusTone[bill.status] }}
        />
        <DocumentParties
          heading="Billed to"
          name={bill.branchName}
          lines={[
            bill.isInterState ? 'Inter-state supply (IGST)' : 'Within the state (CGST + SGST)',
          ]}
          facts={[
            { label: 'Our number', value: bill.documentNumber },
            { label: 'Bill date', value: formatDocumentDate(bill.billDate) },
            { label: 'Due', value: formatDocumentDate(bill.dueDate) },
          ]}
        />
        <DocumentLines columns={columns} rows={bill.lines} rowKey={(line) => line.id} />
        <DocumentTotals
          rows={[
            { label: 'Before tax', value: money(bill.taxableValue) },
            ...(bill.isInterState
              ? [{ label: 'IGST', value: money(bill.igstAmount) }]
              : [
                  { label: 'CGST', value: money(bill.cgstAmount) },
                  { label: 'SGST', value: money(bill.sgstAmount) },
                ]),
            ...(bill.roundOff !== 0 ? [{ label: 'Round off', value: money(bill.roundOff) }] : []),
            { label: 'Bill total', value: money(bill.billTotal), grand: true },
            ...(bill.paperTotal !== null
              ? [{ label: 'On their paper', value: money(bill.paperTotal) }]
              : []),
            ...(bill.paidAmount > 0
              ? [
                  { label: 'Paid', value: money(bill.paidAmount) },
                  {
                    label: 'Left to pay',
                    value: money(Math.max(0, bill.billTotal - bill.paidAmount)),
                  },
                ]
              : []),
          ]}
        />
        {can(session, 'PurchaseBill:View') && bill.notes && (
          <p className="text-default border-border-color mt-6 border-t pt-4 text-[length:var(--scale-list-meta)]">
            {bill.notes}
          </p>
        )}
      </DocumentPaper>
    </>
  );
}

function ReasonAction({
  label,
  title,
  hint,
  busy,
  danger = true,
  onConfirm,
}: {
  label: string;
  title: string;
  hint: string;
  busy: boolean;
  danger?: boolean;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');

  return (
    <Popconfirm
      title={title}
      description={
        <div className="w-60 pt-1">
          <p className="text-default mb-1.5 text-[12px]">{hint}</p>
          <input
            type="text"
            placeholder="Why?"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            className="border-border-color h-8 w-full rounded border bg-white px-2 text-[12px] text-gray-900"
          />
        </div>
      }
      okText={label}
      okButtonProps={{ danger, loading: busy, disabled: reason.trim().length === 0 }}
      cancelText="Back"
      onConfirm={() => {
        onConfirm(reason.trim());
        setReason('');
      }}
      placement="bottomRight"
    >
      <Button disabled={busy}>{label}</Button>
    </Popconfirm>
  );
}
